import { DollarSign, RefreshCw, ArrowDown, ArrowUp } from 'lucide-react';
import type { SitePrices } from '../../api/client';

interface SimulatedPricesCardProps {
  prices: SitePrices | null;
  loading?: boolean;
  error?: string | null;
  isAdmin?: boolean;
  onRefresh?: () => void;
  refreshing?: boolean;
}

// Color based on price level ($/kWh) - solid background for chips
function getChipBgColor(priceKwh: number): string {
  if (priceKwh < 0) return 'bg-green-500';
  if (priceKwh < 0.15) return 'bg-green-400';
  if (priceKwh < 0.20) return 'bg-emerald-400';
  if (priceKwh < 0.30) return 'bg-yellow-400';
  if (priceKwh < 0.40) return 'bg-orange-400';
  if (priceKwh < 0.50) return 'bg-red-400';
  return 'bg-red-600';
}

function formatPrice(priceKwh: number): string {
  return `${(priceKwh * 100).toFixed(1)}c`;
}

function formatTime(isoString: string): string {
  const date = new Date(isoString);
  return date.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', hour12: false });
}

// Price chip component
function PriceChip({
  time,
  price,
  isCurrent,
  isForecast,
}: {
  time: string;
  price: number;
  isCurrent?: boolean;
  isForecast?: boolean;
}) {
  const bgColor = getChipBgColor(price);

  return (
    <div
      className={`
        flex flex-col items-center p-2 rounded-lg transition-all min-w-[52px]
        ${bgColor}
        ${isCurrent
          ? 'shadow-lg scale-110 ring-2 ring-offset-2 ring-offset-gray-100 dark:ring-offset-gray-800 ring-white/50'
          : 'opacity-70 hover:opacity-90'}
        ${isForecast && !isCurrent ? 'border border-dashed border-white/20' : ''}
      `}
    >
      <span className="text-[10px] text-white/70 font-medium">
        {time}
      </span>
      <span className="text-sm text-white font-bold drop-shadow-sm">
        {formatPrice(price)}
      </span>
      {isCurrent && (
        <span className="text-[8px] text-white bg-black/30 px-1.5 py-0.5 rounded mt-1 font-medium">
          NOW
        </span>
      )}
    </div>
  );
}

export function SimulatedPricesCard({
  prices,
  loading,
  error,
  isAdmin,
  onRefresh,
  refreshing,
}: SimulatedPricesCardProps) {
  if (loading) {
    return (
      <div className="card p-4">
        <div className="flex items-center gap-2 mb-4">
          <DollarSign className="w-5 h-5 text-brand-orange" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Simulated Prices</h3>
        </div>
        <div className="flex items-center justify-center h-24">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-orange"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card p-4">
        <div className="flex items-center gap-2 mb-4">
          <DollarSign className="w-5 h-5 text-brand-orange" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Simulated Prices</h3>
        </div>
        <p className="text-sm text-red-500 dark:text-red-400">{error}</p>
      </div>
    );
  }

  if (!prices) {
    return (
      <div className="card p-4">
        <div className="flex items-center gap-2 mb-4">
          <DollarSign className="w-5 h-5 text-brand-orange" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Simulated Prices</h3>
        </div>
        <p className="text-sm text-gray-500 dark:text-gray-400">No price data available</p>
      </div>
    );
  }

  // Build price windows: current + next 6 (7 total)
  const now = new Date();
  const currentWindowStart = new Date(now);
  currentWindowStart.setMinutes(Math.floor(now.getMinutes() / 30) * 30, 0, 0);

  const priceWindows: { time: string; buyPrice: number; sellPrice: number; isCurrent: boolean }[] = [];

  // Current window
  priceWindows.push({
    time: formatTime(currentWindowStart.toISOString()),
    buyPrice: prices.current.import,
    sellPrice: prices.current.export,
    isCurrent: true,
  });

  // Next 6 windows from forecast
  if (prices.forecast && prices.forecast.length > 0) {
    // Get next 6 forecast points (each is 30 min)
    const forecastToShow = prices.forecast.slice(0, 6);
    forecastToShow.forEach((f) => {
      priceWindows.push({
        time: formatTime(f.timestamp),
        buyPrice: f.import,
        sellPrice: f.export,
        isCurrent: false,
      });
    });
  } else {
    // Generate placeholder windows if no forecast
    for (let i = 1; i <= 6; i++) {
      const windowTime = new Date(currentWindowStart.getTime() + i * 30 * 60 * 1000);
      priceWindows.push({
        time: formatTime(windowTime.toISOString()),
        buyPrice: prices.current.import,
        sellPrice: prices.current.export,
        isCurrent: false,
      });
    }
  }

  return (
    <div className="card p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <DollarSign className="w-5 h-5 text-brand-orange" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Simulated Prices
          </h3>
          <span className="text-xs px-2 py-0.5 rounded bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400">
            Simulation
          </span>
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && onRefresh && (
            <button
              onClick={onRefresh}
              disabled={refreshing}
              className="p-1.5 rounded-lg bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-700 transition-colors disabled:opacity-50"
              title="Refresh Prices"
            >
              <RefreshCw className={`w-4 h-4 text-brand-orange ${refreshing ? 'animate-spin' : ''}`} />
            </button>
          )}
          {prices.timestamp && (
            <span className="text-xs text-gray-400">
              Updated {new Date(prices.timestamp).toLocaleTimeString('en-AU')}
            </span>
          )}
        </div>
      </div>

      {/* Buy and Sell Prices Side by Side */}
      <div className="grid grid-cols-2 gap-4 mb-4">
        {/* Buy (Import) Prices */}
        <div>
          <div className="flex items-center gap-2 mb-2">
            <ArrowDown className="w-4 h-4 text-orange-500" />
            <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              Buy (Import)
            </span>
          </div>
          <div className="overflow-x-auto py-1">
            <div className="flex gap-2 items-end pb-1">
              {priceWindows.map((window, idx) => (
                <PriceChip
                  key={`buy-${idx}`}
                  time={window.time}
                  price={window.buyPrice}
                  isCurrent={window.isCurrent}
                  isForecast={!window.isCurrent}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Sell (Export) Prices */}
        <div>
          <div className="flex items-center gap-2 mb-2">
            <ArrowUp className="w-4 h-4 text-green-500" />
            <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              Sell (Export)
            </span>
          </div>
          <div className="overflow-x-auto py-1">
            <div className="flex gap-2 items-end pb-1">
              {priceWindows.map((window, idx) => (
                <PriceChip
                  key={`sell-${idx}`}
                  time={window.time}
                  price={window.sellPrice}
                  isCurrent={window.isCurrent}
                  isForecast={!window.isCurrent}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="flex items-center justify-center gap-3 sm:gap-5 pt-3 border-t border-gray-100 dark:border-gray-700/50 text-[11px] text-gray-500 dark:text-gray-400">
        <div className="flex items-center gap-1.5">
          <div className="w-2.5 h-2.5 rounded-sm bg-green-400"></div>
          <span>&lt;20c</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-2.5 h-2.5 rounded-sm bg-yellow-400"></div>
          <span>20-30c</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-2.5 h-2.5 rounded-sm bg-orange-400"></div>
          <span>30-40c</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-2.5 h-2.5 rounded-sm bg-red-400"></div>
          <span>&gt;40c</span>
        </div>
      </div>
    </div>
  );
}

export default SimulatedPricesCard;
